import type { Node as AstNode } from 'unist';

import type { Block } from '@gitbook/slate';

import type { ParsingOptions } from './options';

/**
 * An index of node key to their start and end index in the input text.
 */
export type ParsingNodeIndexes = Map<string, [number, number]>;

export interface ParsingContext extends ParsingOptions {
    /**
     * Index of node key to their start and end index in the input text.
     */
    nodeIndexes: ParsingNodeIndexes;

    /**
     * Allocate the nodes for a footnote.
     */
    setDefinitionForFootnoteId: (id: string, definition: () => Promise<Block[]>) => void;

    /**
     * Get the fragment for a footnote ID.
     */
    getDefinitionForFootnoteId: (ridef: string) => Promise<Block[]>;

    /**
     * Index the relation between an AST node and a Slate node.
     */
    indexNode: <T extends { key: string }>(astNode: AstNode, node: T) => T;
}

/**
 * Create the local context for parsing.
 */
export function createParsingContext(options: ParsingOptions): ParsingContext {
    const footnoteDefinitions = new Map<string, () => Promise<Block[]>>();
    const nodeIndexes: ParsingNodeIndexes = new Map();

    return {
        ...options,
        nodeIndexes,
        setDefinitionForFootnoteId: (ref, definition) => {
            footnoteDefinitions.set(ref, definition);
        },
        getDefinitionForFootnoteId: async (id) => {
            return footnoteDefinitions.get(id)?.() || [];
        },
        indexNode: (astNode, node) => {
            const startOffset = astNode.position?.start?.offset;
            const endOffset = astNode.position?.end?.offset;

            if (typeof startOffset === 'number' && typeof endOffset === 'number') {
                nodeIndexes.set(node.key, [startOffset, endOffset]);
            }
            return node;
        },
    };
}
